import { useState } from "react";
import { useQuery } from "wasp/client/operations";
import {
  createAccountAttachmentUploadUrl,
  finalizeAccountAttachment,
  getPayoutsByAccount,
} from "wasp/client/operations";
import type { Payout } from "wasp/entities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import { Label } from "../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import useTranslation from "../../client/hooks/useTranslation";
import {
  ATTACHMENT_MIME_TYPES,
  MAX_ATTACHMENT_SIZE_BYTES,
  type AttachmentMimeType,
  type AttachmentType,
} from "../validation";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  initialPayoutId?: string | null;
  initialType?: AttachmentType;
  onSuccess?: () => void;
};

export function AttachmentUploadDialog({
  open,
  onOpenChange,
  accountId,
  initialPayoutId = null,
  initialType = "other",
  onSuccess,
}: Props) {
  const t = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState<AttachmentType>(initialType);
  const [payoutId, setPayoutId] = useState<string | null>(initialPayoutId);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: payouts } = useQuery(getPayoutsByAccount, { accountId });

  function isAllowedMime(mt: string): mt is AttachmentMimeType {
    return (ATTACHMENT_MIME_TYPES as readonly string[]).includes(mt);
  }

  async function handleSubmit() {
    if (!file) return;
    setError(null);

    if (!isAllowedMime(file.type)) {
      setError(t.attachments.errorMimeRejected);
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setError(t.attachments.errorTooLarge);
      return;
    }

    setBusy(true);
    try {
      const { s3UploadUrl, s3UploadFields, s3Key } =
        await createAccountAttachmentUploadUrl({
          accountId,
          fileName: file.name,
          mimeType: file.type,
        });

      const fd = new FormData();
      Object.entries(s3UploadFields).forEach(([k, v]) => fd.append(k, v as string));
      fd.append("file", file);

      const res = await fetch(s3UploadUrl, { method: "POST", body: fd });
      if (!res.ok) throw new Error("s3 upload failed");

      await finalizeAccountAttachment({
        accountId,
        payoutId: type === "payout" ? payoutId : null,
        type,
        name: name.trim() || file.name,
        s3Key,
        mimeType: file.type,
      });

      onSuccess?.();
      onOpenChange(false);
      setName("");
      setFile(null);
      setPayoutId(null);
      setType(initialType);
    } catch (e: unknown) {
      const msg: string = (e as { message?: string })?.message ?? "";
      if (msg.includes("already has a certificate")) {
        setError(t.attachments.errorPayoutTaken);
      } else if (msg.toLowerCase().includes("limit reached")) {
        setError(t.attachments.limitReached);
      } else if (msg.toLowerCase().includes("too large")) {
        setError(t.attachments.errorTooLarge);
      } else if (msg.toLowerCase().includes("does not match")) {
        setError(t.attachments.errorMimeRejected);
      } else {
        setError(t.attachments.errorUpload);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.attachments.uploadDialogTitle}</DialogTitle>
          <DialogDescription>{t.attachments.uploadDialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="att-name">{t.attachments.fieldName}</Label>
            <Input
              id="att-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label>{t.attachments.fieldType}</Label>
            <Select value={type} onValueChange={(v) => setType(v as AttachmentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="challenge">{t.attachments.typeChallenge}</SelectItem>
                <SelectItem value="payout">{t.attachments.typePayout}</SelectItem>
                <SelectItem value="invoice">{t.attachments.typeInvoice}</SelectItem>
                <SelectItem value="other">{t.attachments.typeOther}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "payout" && (
            <div>
              <Label>{t.attachments.fieldLinkedPayout}</Label>
              <Select
                value={payoutId ?? "__none__"}
                onValueChange={(v) => setPayoutId(v === "__none__" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">
                    {t.attachments.fieldLinkedPayoutNone}
                  </SelectItem>
                  {(payouts ?? []).map((p: Payout) => (
                    <SelectItem key={p.id} value={p.id}>
                      {new Date(p.date).toLocaleDateString()} — ${p.amount}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label htmlFor="att-file">{t.attachments.fieldFile}</Label>
            <Input
              id="att-file"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t.common.cancel}
          </Button>
          <Button onClick={handleSubmit} disabled={!file || busy}>
            {busy ? t.attachments.uploading : t.attachments.uploadButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
